import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { FeatureFlags, SearchType } from '@aibees/elephant/types';
import { useBootstrapApiBase } from '@aibees/shared/api';
import { Icon } from '@aibees/shared/components';
import { CreditUsageType } from '@aibees/shared/definition';
import { useFeatureFlagsContext } from '@aibees/shared/feature-flags';
import { classnames } from '@aibees/shared/utils';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { InformNotificationPopup } from '../inform-notification-popup';
import { SavedRecordsUpdateDialog } from './components/saved-records-update-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import { EnrichButtonDialogType, EnrichButtonProps as Props } from './types';
import {
  BULK_UPDATE_ACTION_ORDER,
  BULK_UPDATE_ACTIONS,
} from './update-actions';

export const EnrichButton = memo(
  ({ className, disabled, isSmall, table }: Props) => {
    const type = useSearchTypeFromRoute();

    const isEnabled = useFeatureFlagsContext().isFeatureEnabled(
      FeatureFlags.PEOPLE_ENRICHMENT
    );

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialogType, setDialogType] = useState<EnrichButtonDialogType | null>(
      null
    );

    // Every price in this menu is server-driven — see the bootstrap cost map.
    const cost = useBootstrapApiBase().data?.cost;
    const getCost = useCallback(
      (creditType: CreditUsageType) => cost?.[creditType] ?? 0,
      [cost]
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleCloseDialog = () => {
      setDialogType(null);
      handleClose();
    };

    const handleOpenDialog = (value: EnrichButtonDialogType) => {
      setDialogType(value);
      handleClose();
    };

    if (!isEnabled || type !== SearchType.People) {
      return null;
    }

    return (
      <Styled.Wrapper
        className={classnames('EnrichButton', className)}
        sx={SX.root}
      >
        {/* Only the open one is mounted. Each instance runs a preflight and a
            save-record mutation hook, and at most one dialog is ever open. */}
        {dialogType && (
          <SavedRecordsUpdateDialog
            open
            onClose={handleCloseDialog}
            config={BULK_UPDATE_ACTIONS[dialogType]}
            table={table}
          />
        )}
        <Tooltip
          className={classnames('SearchTableActionDialogLists', className)}
          sx={SX.root}
          title={disabled ? 'Please select some records first' : undefined}
          arrow
        >
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="outline-find-replace" size={18} />}
              endIcon={
                !isSmall && (
                  <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
                )
              }
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              disabled={disabled}
            >
              {!isSmall && (
                <Stack direction="row" alignItems="center" component="span">
                  Enrich
                </Stack>
              )}
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {BULK_UPDATE_ACTION_ORDER.map((key) => {
                const item = BULK_UPDATE_ACTIONS[key];

                return (
                  <MenuItem
                    key={key}
                    onClick={() => handleOpenDialog(key)}
                    sx={SX.menuItem}
                  >
                    <Stack justifyContent="center" alignItems="center">
                      <Icon name={item.icon} size={18} />
                    </Stack>
                    <Typography variant="body4" color="grey.700">
                      {item.label}
                    </Typography>
                    {/* Stop the click so reading the tooltip does not open the
                        preflight dialog. */}
                    <Stack
                      sx={SX.menuItemInfo}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <InformNotificationPopup>
                        {item.tooltip(getCost)}
                      </InformNotificationPopup>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        </Tooltip>
      </Styled.Wrapper>
    );
  }
);

EnrichButton.displayName = 'EnrichButton';
